import { writable } from 'svelte/store';
import collect from 'collect.js';
import Promotion from '@/Models/Promotion';
import dayjs from 'dayjs';
import { mergeOrAddModelDataInCollectionStore } from '@/Stores/StoreHelpers';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getPromotions(
			promoType = null,
			activeOnly = false,
			pagination = {
				perPage: 30,
				page: 1
			},
			onlySystem = false,
			includeCampaigns = false
		) {
			let promotion = new Promotion();
			if (promoType) {
				promotion = promotion.where('type', promoType);
			}
			if (activeOnly) {
				promotion = promotion.where('expired', false);
			}
			if (onlySystem === true) {
				promotion = promotion.where('is_system', true);
			} else if (onlySystem === false) {
				promotion = promotion.where('is_system', false);
			}
			if (!includeCampaigns) {
				promotion = promotion.where('campaign_id', null);
			}

			return promotion.paginate(pagination.perPage, pagination.page).then((result) => {
				if (result.meta) {
					meta = result.meta;
				}
				set(collect(result.data));
			});
		},

		getPromotionById(promotionId) {
			let promotion = new Promotion();

			return promotion.find(promotionId).then((result) => {
				mergeOrAddModelDataInCollectionStore(result, this);
			});
		},

		searchPromotion(
			keyword,
			promoType,
			pagination = null,
			activeOnly = true,
			onlySystem = false,
			includeCampaigns = false
		) {
			let promotion = new Promotion();

			if (activeOnly) {
				promotion = promotion.where('expired', false);
			}
			if (promoType) {
				promotion = promotion.where('type', promoType);
			}
			if (onlySystem === true) {
				promotion = promotion.where('is_system', true);
			} else if (onlySystem === false) {
				promotion = promotion.where('is_system', false);
			}
			if (!includeCampaigns) {
				promotion = promotion.where('campaign_id', null);
			}

			let results = pagination
				? promotion.where('search', keyword).paginate(pagination.size, pagination.number)
				: promotion.where('search', keyword).get();
			return results
				.then((result) => {
					meta = result.meta;
					set(result.data);
				})
				.catch((e) => {
					console.error(e);
				});
		},

		createPromotion(data) {
			let promotion = new Promotion();

			return promotion
				.makePersistRequest({
					url: `${promotion.resourceUrl()}`,
					method: 'PUT',
					data
				})
				.then((result) => {
					return result;
				});
		},

		addUsersToPromotion(promotion, file) {
			return promotion
				.doFileUploadRequest(`${promotion.resourceUrl()}${promotion.id}/users`, 'promotion_user_list', file)
				.then((result) => {
					return result;
				});
		},

		addPromoToUser(user, promotion_id) {
			return user.makePersistRequest({
				url: `${user.baseUrl()}/users/${user.id}/promotions/${promotion_id}`,
				method: 'POST'
			});
		},

		closePromotion(promotionId) {
			update((promotions) => {
				return promotions.each((promotion) => {
					if (promotion.id === promotionId) {
						promotion.expired_at = dayjs().format('YYYY-MM-DDTHH:mm:00Z');
					}
					return promotion;
				});
			});

			let promotion = _store.firstWhere('id', promotionId);
			return promotion
				.patch({
					expired_at: promotion.expired_at
				})
				.then((results) => {
					return results;
				});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
